import type { PropostaItem, SupportPricingTier } from '@/types'
import { buildRocketSearchGalaxyConfig } from '../../../../server/src/data/planRocketSearchGalaxy'
import {
  findCatalogPlanForItem,
  getScopeFromCatalog,
  isRocketCatalogPlan,
  type CatalogPlan,
} from '@/utils/planCatalog'
import {
  hasMultipleSupportTiers,
  normalizeSupportTiers,
  primarySupportTier,
} from '@/utils/planSuportePricing'
import {
  DEFAULT_SERVICES_TEMPLATE,
  type PlanEditorCategoryGroup,
  type PlanEditorServiceItem,
} from './planEditorConstants'

function normalizeKey(value: string): string {
  return value.toLowerCase().trim()
}

export function mergePlanServicesWithTemplate(
  planServices: PlanEditorCategoryGroup[],
): PlanEditorCategoryGroup[] {
  if (planServices.length > 0) {
    return planServices.map((cat) => ({
      category: cat.category,
      services: cat.services.map((s) => ({
        name: s.name,
        isIncluded: s.isIncluded ?? false,
        isPartial: s.isPartial ?? false,
        quantity: s.quantity || '',
      })),
    }))
  }

  const mergedList = DEFAULT_SERVICES_TEMPLATE.map((tplCat) => {
    const matchingPlanCat = planServices.find(
      (pc) => normalizeKey(pc.category) === normalizeKey(tplCat.category),
    )
    const services = tplCat.services.map((tplSvc) => {
      if (matchingPlanCat) {
        const matchingPlanSvc = matchingPlanCat.services.find(
          (ps) => normalizeKey(ps.name) === normalizeKey(tplSvc.name),
        )
        if (matchingPlanSvc) {
          return {
            name: matchingPlanSvc.name,
            isIncluded: matchingPlanSvc.isIncluded ?? false,
            isPartial: matchingPlanSvc.isPartial ?? false,
            quantity: matchingPlanSvc.quantity || '',
          }
        }
      }
      return { ...tplSvc }
    })

    if (matchingPlanCat) {
      matchingPlanCat.services.forEach((ps) => {
        const alreadyAdded = services.some((s) => normalizeKey(s.name) === normalizeKey(ps.name))
        if (!alreadyAdded) {
          services.push({
            name: ps.name,
            isIncluded: ps.isIncluded ?? false,
            isPartial: ps.isPartial ?? false,
            quantity: ps.quantity || '',
          })
        }
      })
    }

    return { category: tplCat.category, services }
  })

  planServices.forEach((ps) => {
    const categoryExists = mergedList.some(
      (ml) => normalizeKey(ml.category) === normalizeKey(ps.category),
    )
    if (!categoryExists) {
      mergedList.push({
        category: ps.category,
        services: ps.services.map((s) => ({
          name: s.name,
          isIncluded: s.isIncluded ?? false,
          isPartial: s.isPartial ?? false,
          quantity: s.quantity || '',
        })),
      })
    }
  })

  return mergedList
}

function toEditorGroups(raw: unknown): PlanEditorCategoryGroup[] {
  if (!Array.isArray(raw)) return []
  return raw.map((cat) => {
    const group = cat as {
      category?: string
      categoria?: string
      services?: Array<{
        name?: string
        nome?: string
        isIncluded?: boolean
        isPartial?: boolean
        quantity?: string | number
        quantidade?: string | number
      }>
    }
    return {
      category: String(group.category || group.categoria || 'Geral'),
      services: (group.services || []).map((service) => ({
        name: String(service.name || service.nome || ''),
        isIncluded: service.isIncluded !== false,
        isPartial: service.isPartial === true,
        quantity: String(service.quantity || service.quantidade || ''),
      })),
    }
  })
}

export function isLegacyMarketingScope(groups: PlanEditorCategoryGroup[]): boolean {
  const blob = groups
    .map((group) => `${group.category} ${group.services.map((service) => service.name).join(' ')}`)
    .join(' ')
    .toLowerCase()
  return blob.includes('google ads') || blob.includes('tráfego') || blob.includes('trafego')
}

function isRocketEditorPlan(plan: PropostaItem, catalogPlans: CatalogPlan[]): boolean {
  const master = findCatalogPlanForItem(plan, catalogPlans)
  if (
    isRocketCatalogPlan({
      nome: plan.descricao || plan.nome || plan.name,
      category: master?.category,
      config: master?.config,
    })
  ) {
    return true
  }
  if (master && isRocketCatalogPlan(master)) return true
  return catalogPlans.length > 0 && catalogPlans.every((item) => isRocketCatalogPlan(item))
}

/** Escopo do editor: plano Rocket usa o Galaxy, nunca o template de tráfego. */
export function resolvePlanEditorScope(
  plan: PropostaItem,
  catalogPlans: CatalogPlan[] = [],
): PlanEditorCategoryGroup[] {
  const master = findCatalogPlanForItem(plan, catalogPlans)
  const catalogScope = master ? toEditorGroups(getScopeFromCatalog(master)) : []
  const savedScope = toEditorGroups(plan.services)
  const galaxyScope = toEditorGroups(buildRocketSearchGalaxyConfig().services)

  if (isRocketEditorPlan(plan, catalogPlans)) {
    if (catalogScope.length > 0 && !isLegacyMarketingScope(catalogScope)) return catalogScope
    return galaxyScope
  }

  if (catalogScope.length > 0 && !isLegacyMarketingScope(catalogScope)) return catalogScope
  if (savedScope.length > 0 && !isLegacyMarketingScope(savedScope)) return savedScope
  return mergePlanServicesWithTemplate(savedScope)
}

export function countIncludedServices(servicesList: PlanEditorCategoryGroup[]): number {
  return servicesList.reduce(
    (acc, cat) => acc + cat.services.filter((s) => s.isIncluded).length,
    0,
  )
}

export function buildPlanEditorItem(
  plan: PropostaItem,
  params: {
    descricao: string
    tipo: PropostaItem['tipo']
    quantidade: number
    duration: number
    pricingModel: 'recorrente' | 'setup'
    valorParcela: number
    valorTotal: number
    valorEntrada: number
    numParcelas: number
    showBonus: boolean
    bonusText: string
    servicesList: PlanEditorCategoryGroup[]
    supportHours?: number
    hourlyRate?: number
    supportTiers?: SupportPricingTier[]
    supportPricingMode?: 'FIXO_2000' | 'BASE_500_MAIS_1_FATURAMENTO'
    monthlyFixedValue?: number
    monthlyBaseValue?: number
    revenueSharePercent?: number
  },
): PropostaItem {
  const {
    descricao,
    tipo,
    quantidade,
    duration,
    valorParcela,
    valorEntrada,
    numParcelas,
    showBonus,
    bonusText,
    servicesList,
  } = params

  const hasSuportePricing =
    params.supportHours != null &&
    params.supportHours > 0 &&
    params.hourlyRate != null &&
    params.hourlyRate > 0
  const normalizedTiers = normalizeSupportTiers(params.supportTiers)
  const multiTiers = hasMultipleSupportTiers(normalizedTiers)
  const primaryTier = primarySupportTier(normalizedTiers)

  const resolvedParcela =
    multiTiers && primaryTier ? primaryTier.monthlyValue : valorParcela
  const resolvedSupportHours =
    multiTiers && primaryTier ? primaryTier.supportHours : params.supportHours

  const finalUnitValue = resolvedParcela
  const finalTotal = (valorEntrada + duration * resolvedParcela) * quantidade

  return {
    ...plan,
    descricao: descricao.trim(),
    tipo,
    valorUnitario: finalUnitValue,
    quantidade,
    total: finalTotal,
    duration,
    showBonus,
    bonus: bonusText.trim(),
    pricing: {
      total: finalTotal / quantidade,
      entry: valorEntrada,
      installments: numParcelas,
      installmentValue: resolvedParcela,
      recorrente: true,
      duracao: duration,
      supportPricingMode: params.supportPricingMode,
      monthlyFixedValue: params.monthlyFixedValue,
      monthlyBaseValue: params.monthlyBaseValue,
      revenueSharePercent: params.revenueSharePercent,
      ...(hasSuportePricing
        ? {
            supportHours: resolvedSupportHours,
            hourlyRate: params.hourlyRate,
            ...(multiTiers ? { supportTiers: normalizedTiers } : {}),
          }
        : {}),
    },
    services: servicesList,
  }
}

export type { PlanEditorServiceItem, PlanEditorCategoryGroup }
